import { useState } from "react";
import {
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useAppDispatch } from "@store/hooks";
import { createReading } from "@features/workspace-readings/state/workspaceReadingsSlice";

export interface OwnedWorkspace {
  workspaceId: string;
  workspaceName: string;
}

// One document the menu can act on. `linkedWorkspaceIds` is the set of
// workspaces that already have a reading pointing at this document, so
// the menu can skip silent duplicates when adding in bulk.
export interface AddToWorkspaceTarget {
  documentId: string;
  documentName: string;
  linkedWorkspaceIds: Set<string>;
}

interface Props {
  anchorEl: HTMLElement | null;
  // One entry → single-document add (per-row picker).
  // Many entries → bulk add (selection-driven, from the toolbar).
  targets: AddToWorkspaceTarget[];
  ownedWorkspaces: OwnedWorkspace[];
  onClose: () => void;
  // Called after one or more reading creations succeed so the parent can
  // refetch documents and update chips.
  onAdded: () => void;
  // Surface API errors back to the parent's error region. The menu
  // itself is too small for an inline Alert.
  onError: (message: string) => void;
}

const AddToWorkspaceMenu = ({
  anchorEl,
  targets,
  ownedWorkspaces,
  onClose,
  onAdded,
  onError,
}: Props) => {
  const dispatch = useAppDispatch();
  const [submittingWorkspaceId, setSubmittingWorkspaceId] = useState<
    string | null
  >(null);

  // For each owned workspace, how many of the selected targets aren't
  // already linked to it. Workspaces where every target is already
  // linked render disabled — adding would be a no-op.
  const candidatesByWorkspace = ownedWorkspaces.map((ws) => {
    const eligible = targets.filter(
      (t) => !t.linkedWorkspaceIds.has(ws.workspaceId)
    );
    return { workspace: ws, eligible };
  });

  const handleAdd = async (
    ws: OwnedWorkspace,
    eligible: AddToWorkspaceTarget[]
  ) => {
    if (eligible.length === 0) return;
    setSubmittingWorkspaceId(ws.workspaceId);
    const results = await Promise.allSettled(
      eligible.map((t) =>
        dispatch(
          createReading({
            workspace_id: ws.workspaceId,
            document_id: t.documentId,
            reading_name: t.documentName,
            required: false,
            assign_to_all: true,
          })
        ).unwrap()
      )
    );
    setSubmittingWorkspaceId(null);

    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0) {
      onError(
        `Added ${eligible.length - failed} of ${
          eligible.length
        } files; ${failed} failed.`
      );
    }
    if (failed < eligible.length) {
      onAdded();
    }
    onClose();
  };

  const isOpen = Boolean(anchorEl) && targets.length > 0;
  const isBulk = targets.length > 1;

  return (
    <Menu anchorEl={anchorEl} open={isOpen} onClose={onClose}>
      {isBulk && (
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary">
            Add {targets.length} files to…
          </Typography>
        </MenuItem>
      )}
      {ownedWorkspaces.length === 0 ? (
        <MenuItem disabled>
          <Typography variant="body2" color="text.secondary">
            You don't own any workspaces.
          </Typography>
        </MenuItem>
      ) : (
        candidatesByWorkspace.map(({ workspace, eligible }) => {
          const allLinked = eligible.length === 0;
          return (
            <MenuItem
              key={workspace.workspaceId}
              onClick={() => handleAdd(workspace, eligible)}
              disabled={
                allLinked ||
                submittingWorkspaceId === workspace.workspaceId
              }
            >
              <ListItemText
                primary={workspace.workspaceName}
                secondary={
                  isBulk
                    ? allLinked
                      ? "All already in this workspace"
                      : `${eligible.length} of ${targets.length} new`
                    : allLinked
                      ? "Already in this workspace"
                      : null
                }
              />
              {submittingWorkspaceId === workspace.workspaceId && (
                <CircularProgress size={14} sx={{ ml: 1 }} />
              )}
            </MenuItem>
          );
        })
      )}
    </Menu>
  );
};

export default AddToWorkspaceMenu;
